import { useQuery } from "@tanstack/vue-query";
import { useI18n } from "vue-i18n";
import type { MaybeRef } from "vue";
import { api } from "@/helpers/api";
import brandconfig from "@/brandconfig";
import {
  createApiQueryByIdFn,
  createListQueryFn,
  EventsApi,
  useApiQuery,
  type APIQueryOptions,
  type EventsportalcacheGetRequest,
} from "@xs2event/js-lib/api";
import type { QueryClient } from "@tanstack/vue-query";

const TODAY = new Date().toISOString().substr(0, 10);
const eventsApi = new EventsApi();

export const usePopularEvents = () => {
  const { locale } = useI18n();

  return useQuery({
    queryKey: ["popular-events", locale],
    queryFn: async () => {
      const params = new URLSearchParams(brandconfig.quicklist.params || {});
      params.append("date_stop", "ge:" + TODAY);
      params.append("tickets_available", "ge:1");
      params.append("ticket_price_eur", "ge:1");
      params.append("popular_events", true);
      params.append("event_status", "in:[nosale,notstarted,postponed]");
      //params.append('sport_type', 'motorsport')
      params.append("page_size", 5);
      const response = await api.get("/events?" + params);
      // console.log(response.data.events)
      return response.data.events;
    },
    staleTime: 1000 * 60 * 60, // 1 hour
  });
};

export const useEvents = createListQueryFn(eventsApi, "eventsGet");

const PORTAL_EVENTS_CACHE_METHOD = "eventsportalcacheGet";

/**
 * Long enough that one fetch covers a typical browsing session, so the prefetch below actually pays
 * off. Lives here rather than at the call sites: the prefetched entry is only useful while the page
 * still considers it fresh, so the two must not drift apart.
 */
export const PORTAL_EVENTS_CACHE_STALE_TIME = 1000 * 60 * 30; // 30 minutes

/**
 * Every upcoming event (date_start >= today, any status except closed), priced for the calling
 * distributor. Takes no filters and no pagination — the portal filters and sorts client side.
 */
export const usePortalEventsCache = (payload?: MaybeRef<EventsportalcacheGetRequest>, options?: APIQueryOptions) =>
  useApiQuery(eventsApi, PORTAL_EVENTS_CACHE_METHOD, payload, {
    debounce: false,
    staleTime: PORTAL_EVENTS_CACHE_STALE_TIME,
    ...options,
  });

/**
 * Warm the portal events cache from outside a component, so the events listing renders from cache
 * instead of waiting on this (large, unpaginated) response at click time.
 *
 * The query key mirrors the `[method, params]` shape `useApiQuery` builds — keep the two in sync, or
 * the prefetch and `usePortalEventsCache` end up writing to two separate cache entries.
 */
export const prefetchPortalEventsCache = (queryClient: QueryClient, payload: EventsportalcacheGetRequest) =>
  queryClient.prefetchQuery({
    queryKey: [PORTAL_EVENTS_CACHE_METHOD, payload],
    queryFn: () => eventsApi.eventsportalcacheGet(payload),
    staleTime: PORTAL_EVENTS_CACHE_STALE_TIME,
  });

export const useGetEventsByIds = createApiQueryByIdFn(eventsApi, "eventsGet", "eventId");
